import { useEffect, useRef, useState } from "react";
import { Input } from "@/components";
import { LessonUnitDto, LessonDto } from "@/types";
import { useLessonList } from "../hooks/useLessonList";
import { cn } from "@/utils";
import { IoIosSearch } from "react-icons/io";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce } from "@/hooks/useDebounce";
import { KEYS_ELEARNING_SEARCH_PARAMS } from "..";
import { FaAngleRight } from "react-icons/fa6";
import { Lesson } from "./LessonDetail";
import { LessonComponent, LessonPlaceholder } from "./LessonComponent";

const contentHeight = `h-[calc(100vh-52px-60px-48px-1px-32px-20px)]`; // 100vh - header - title - tab -inkbar - padding - search

export const Lessons = () => {
  const [openLessonModal, setOpenLessonModal] = useState(false);
  const [lesson, setLesson] = useState<LessonDto | undefined>(undefined);
  const isAtBottomRef = useRef(false);
  const isInitialMount = useRef(true);
  const refLesson = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);

  const currentSearch = params[KEYS_ELEARNING_SEARCH_PARAMS.SEARCH] || "";

  const [inputValue, setInputValue] = useState<string>(currentSearch);
  const debouncedSearchTerm = useDebounce(inputValue, 500);

  const {
    data: lessonList,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useLessonList(currentSearch);

  const updateSearchParams = (object: { [key: string]: string }) => {
    const inputParams: { [key: string]: string } = params;
    for (const key in object) {
      const value = object[key]?.trim();
      if (value) {
        inputParams[key] = value;
      } else {
        delete inputParams[key];
      }
    }
    setSearchParams(inputParams, { replace: true });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      updateSearchParams({
        [KEYS_ELEARNING_SEARCH_PARAMS.SEARCH]: inputValue?.trim(),
      });
    }
  };

  const onOpenLessonModal = (lesson: LessonDto) => {
    setOpenLessonModal(true);
    setLesson(lesson);
  };

  const onCloseLessonModal = () => {
    setOpenLessonModal(false);
    setLesson(undefined);
  };

  useEffect(() => {
    // Skip updating search params on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debouncedSearchTerm?.trim()) {
      updateSearchParams({
        [KEYS_ELEARNING_SEARCH_PARAMS.SEARCH]:
          debouncedSearchTerm?.trim() || "",
      });
    } else {
      updateSearchParams({ [KEYS_ELEARNING_SEARCH_PARAMS.SEARCH]: "" });
    }
  }, [debouncedSearchTerm]);

  const checkIfAtBottom = () => {
    if (!refLesson.current) return;
    const { scrollTop, scrollHeight, clientHeight } = refLesson.current;
    const isAtBottom = scrollHeight - scrollTop <= clientHeight + 5; // Ngưỡng 5px

    if (isAtBottom && !isAtBottomRef.current) {
      isAtBottomRef.current = true;
      if (hasNextPage) {
        fetchNextPage();
      }
    } else if (!isAtBottom) {
      isAtBottomRef.current = false;
    }
  };

  if (isLoading && lessonList === undefined) {
    return <LessonListPlaceholder />;
  }

  const lessonListFlat = lessonList?.pages.flatMap((page) => page.data) || [];

  return (
    <>
      <section className={cn("space-y-4 relative w-full h-full py-4")}>
        <div className="absolute -top-11 right-0 z-10  bg-white">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="検索"
            icon={<IoIosSearch className="size-4" />}
            className="!h-10 w-80"
            onKeyDown={handleKeyDown}
          />
        </div>

        <div
          className={cn(
            "overflow-y-scroll visible-scrollbar space-y-6",
            contentHeight,
          )}
          ref={refLesson}
          onScroll={checkIfAtBottom}
        >
          {!lessonListFlat?.length && (
            <div className="text-center text-grey-500 py-8">
              <p className="text-sm">データがありません</p>
            </div>
          )}
          {lessonListFlat.map((lessonUnit) => (
            <UnitComponent
              key={lessonUnit.id}
              lessonUnit={lessonUnit}
              onLessonClick={onOpenLessonModal}
            />
          ))}
        </div>
      </section>

      <Lesson
        lesson={lesson}
        open={openLessonModal}
        onClose={onCloseLessonModal}
      />
    </>
  );
};

const LessonListPlaceholder = () => {
  return (
    <section className="py-4">
      {Array(3)
        .fill(1)
        .map((_, idx) => (
          <div key={idx} className="space-y-4 w-full animate-pulse">
            <div className="flex items-center justify-between h-4 w-96 bg-light_grey  rounded-full" />
            <div className="grid grid-cols-5 gap-6">
              {Array(5)
                .fill(1)
                .map((_, index) => (
                  <LessonPlaceholder key={`${idx}${index}`} />
                ))}
            </div>
          </div>
        ))}
    </section>
  );
};

const UnitComponent = ({
  lessonUnit,
  onLessonClick,
}: {
  lessonUnit: LessonUnitDto;
  onLessonClick: (lesson: LessonDto) => void;
}) => {
  const navigate = useNavigate();
  const onGetListLesson = (unitId: number) => {
    navigate(`/e-learning/units/${unitId}`);
  };

  if (!lessonUnit.lessons?.length) return null;

  return (
    <div className="space-y-4 text-dark w-full">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold leading-6 truncate max-w-[500px]">
          {lessonUnit.title}
        </h2>
        <button
          className="flex flex-grow-0 items-center justify-center gap-2 text-cyan hover:underline"
          onClick={() => {
            onGetListLesson(lessonUnit.id);
          }}
        >
          <p className="font-semibold leading-5.5 text-sm">全て表示</p>
          <FaAngleRight className="w-5" />
        </button>
      </div>
      {/* lesson  */}
      <div className="grid grid-cols-5 gap-6">
        {lessonUnit.lessons.map((lesson) => (
          <LessonComponent
            key={lesson.id}
            lesson={lesson}
            onLessonClick={onLessonClick}
          />
        ))}
      </div>
    </div>
  );
};
